import { Page } from "@/types/pagination";
import {
  PatientTag,
  PatientTagCreate,
  PatientTagUpdate,
} from "@/types/patient-tag";

import { requestData } from "@/api/api-config";

export interface PatientTagAssignment {
  patientId: string;
  tagIds: string[];
}

// Patient Tag CRUD Operations
// catchError=false makes a failure throw instead of resolving null. Callers that
// have to tell "no tags" apart from "could not load" need that distinction; the
// default keeps the swallow-to-null behaviour every existing caller relies on.
export async function getPatientTags(
  catchError = true,
  includeArchived = false,
): Promise<Page<PatientTag> | null> {
  return await requestData<Page<PatientTag>>(
    "user/patient/tags/list",
    "get",
    includeArchived ? { searchParams: { includeArchived: true } } : undefined,
    catchError,
  );
}

export async function createPatientTag(
  data: PatientTagCreate,
): Promise<PatientTag | null> {
  return await requestData<PatientTag>("user/patient/tags", "post", {
    json: data,
  });
}

export async function getPatientTag(tagId: string): Promise<PatientTag | null> {
  return await requestData<PatientTag>(`user/patient/tags/${tagId}`, "get");
}

export async function updatePatientTag(
  tagId: string,
  data: PatientTagUpdate,
): Promise<PatientTag | null> {
  return await requestData<PatientTag>(`user/patient/tags/${tagId}`, "put", {
    json: data,
  });
}

export async function deletePatientTag(
  tagId: string,
): Promise<{ message: string } | null> {
  return await requestData<{ message: string }>(
    `user/patient/tags/${tagId}`,
    "delete",
  );
}

// Patient Tag Assignment Operations
// Returns the patient's resulting tag set (read back on the primary), so the
// caller can update its cache without a replica read that may lag the write.
// catchError=false so failures throw and the mutation's onError can revert the
// optimistic cache update.
export async function assignPatientTags(
  patientId: string,
  tagIds: string[],
): Promise<PatientTag[] | null> {
  return await requestData<PatientTag[]>(
    `user/patient/${patientId}/tags`,
    "post",
    {
      json: { patientId, tagIds },
    },
    false,
  );
}

export async function getPatientTagsForPatient(
  patientId: string,
): Promise<PatientTag[] | null> {
  return await requestData<PatientTag[]>(
    `user/patient/${patientId}/tags`,
    "get",
  );
}

export interface BulkPatientTagAssignmentResponse {
  message: string;
  assignedCount: number;
}

export async function bulkAssignPatientTags(
  patientIds: string[],
  tagIds: string[],
): Promise<BulkPatientTagAssignmentResponse | null> {
  // catchError=false so failures throw and React Query's onError can revert
  // the optimistic cache update.
  return await requestData<BulkPatientTagAssignmentResponse>(
    "user/patient/tags/bulk-assign",
    "post",
    {
      json: { patientIds, tagIds },
    },
    false,
  );
}
